import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button, Form, Input, Modal, notification, Spin } from "antd";
import { PlusOutlined, ImportOutlined } from "@ant-design/icons";
import { ImportFromUrlModal } from "../../molecules/import-from-url-modal/ImportFromUrlModal";
import { useTourContext } from "../../../contexts/tour-context/useTourContext";
import { useImpersonation } from "../../../contexts/impersonation-context/ImpersonationContext";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getProductCategories,
  changeCategoriesOrder,
  createProductCategory,
} from "../../../service";
import { ProductCategorySection } from "../../molecules/menu-product-category-section/ProductCategorySection";
import { colors, spacing } from "../../../styles/theme";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.xl};
`;

const TabDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  max-width: 600px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

export const ProductsTab: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategoryId, setNewCategoryId] = useState<number | null>(null);
  const [scrollTargetCategoryId, setScrollTargetCategoryId] = useState<number | null>(null);
  const [scrollTargetProductId, setScrollTargetProductId] = useState<number | null>(null);
  const { addCategoryBtnRef, categoryNameInputRef, categoryModalOkBtnRef, addProductBtnRef, isTourOpen, tourStep, stepIndexes, advanceTourStep } = useTourContext();
  const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    const state = location.state as { scrollToCategoryId?: number; scrollToProductId?: number } | null;
    if (state?.scrollToCategoryId) {
      setScrollTargetCategoryId(state.scrollToCategoryId);
      setScrollTargetProductId(state.scrollToProductId ?? null);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  const { isLoading, data: queryData } = useQuery<any, Error>(
    ["/product-categories"],
    () => getProductCategories(),
    {
      onSuccess(data) {
        setCategories(data);
      },
    }
  );

  // Synchronizuj lokalny stan z query cache (dla optimistic updates z edycji kategorii)
  useEffect(() => {
    if (queryData && Array.isArray(queryData)) {
      setCategories((currentCategories) => {
        if (currentCategories.length === 0) {
          return queryData;
        }
        // Mapuj aktualne kategorie z nowymi danymi, zachowując kolejność
        const currentIds = currentCategories.map((c) => c.id);
        const updatedCategories = currentCategories.map((cat) => {
          const updated = queryData.find((q: any) => q.id === cat.id);
          return updated ? { ...cat, ...updated } : cat;
        });
        // Dodaj nowe kategorie (jeśli są)
        const newCategories = queryData.filter((q: any) => !currentIds.includes(q.id));
        return [...updatedCategories, ...newCategories];
      });
    }
  }, [queryData]);

  const { mutateAsync: changeCategoryOrderMutation } = useMutation(
    changeCategoriesOrder,
    {
      onSuccess: (data) => {
        setCategories(data);
        queryClient.invalidateQueries(["/product-categories"]);
      },
    }
  );

  const { isDeveloper } = useImpersonation();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [addCategoryForm] = Form.useForm();

  const { mutateAsync: createCategoryMutation, isLoading: isCreatingCategory } =
    useMutation(createProductCategory, {
      onSuccess: async (data: any) => {
        notification.success({
          message: t("productsTab.categoryCreated"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        addCategoryForm.resetFields();
        setAddCategoryModalOpen(false);

        if (isTourOpen) {
          advanceTourStep(stepIndexes.CATEGORY_MODAL_OPEN);
        }

        if (data?.id) {
          // Pobierz aktualną listę kategorii i przenieś nową na koniec
          const updatedCategories = await getProductCategories();
          const newCategoryIndex = updatedCategories.findIndex(
            (cat: any) => cat.id === data.id
          );

          if (newCategoryIndex !== -1 && newCategoryIndex !== updatedCategories.length - 1) {
            // Nowa kategoria nie jest na końcu - przenieś ją
            const newCategory = updatedCategories.splice(newCategoryIndex, 1)[0];
            updatedCategories.push(newCategory);

            // Zapisz nową kolejność
            const orderedIds = updatedCategories.map((cat: any) => cat.id);
            await changeCategoryOrderMutation({ categoriesIds: orderedIds });
          } else {
            // Kategoria już jest na końcu lub jedyna
            setCategories(updatedCategories);
          }

          // Zapisz ID do scrollowania
          setNewCategoryId(data.id);
        } else {
          queryClient.invalidateQueries(["/product-categories"]);
        }


      },
      onError: (error: any) => {
        notification.error({
          message: t("productsTab.categoryCreateError"),
          description: error?.response?.data?.message || t("productsTab.tryAgain"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    });

  // Scroll do nowo utworzonej kategorii
  useEffect(() => {
    if (newCategoryId && categoryRefs.current[newCategoryId]) {
      setTimeout(() => {
        categoryRefs.current[newCategoryId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setNewCategoryId(null);
      }, 100);
    }
  }, [newCategoryId, categories]);

  // Scroll do kategorii po powrocie z edycji produktu
  useEffect(() => {
    if (scrollTargetCategoryId && categoryRefs.current[scrollTargetCategoryId] && categories.length > 0) {
      setTimeout(() => {
        categoryRefs.current[scrollTargetCategoryId]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [scrollTargetCategoryId, categories]);

  const handleMoveCategoryUp = async (categoryId: number) => {
    const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
    if (currentIndex <= 0) return;

    const newCategories = [...categories];
    [newCategories[currentIndex - 1], newCategories[currentIndex]] = [
      newCategories[currentIndex],
      newCategories[currentIndex - 1],
    ];

    const orderedIds = newCategories.map((cat) => cat.id);
    await changeCategoryOrderMutation({ categoriesIds: orderedIds });
  };

  const handleMoveCategoryDown = async (categoryId: number) => {
    const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
    if (currentIndex < 0 || currentIndex >= categories.length - 1) return;

    const newCategories = [...categories];
    [newCategories[currentIndex], newCategories[currentIndex + 1]] = [
      newCategories[currentIndex + 1],
      newCategories[currentIndex],
    ];

    const orderedIds = newCategories.map((cat) => cat.id);
    await changeCategoryOrderMutation({ categoriesIds: orderedIds });
  };

  const handleAddCategory = () => {
    setAddCategoryModalOpen(true);
    // Krok 3 tour: kliknięcie "Dodaj Kategorię" → przejdź do kroku 4
    if (isTourOpen && tourStep === stepIndexes.ADD_CATEGORY_BTN) advanceTourStep(stepIndexes.ADD_CATEGORY_BTN);
  };

  const handleAddCategorySubmit = async () => {
    try {
      const values = await addCategoryForm.validateFields();
      await createCategoryMutation({ name: values.name });
    } catch {
      // validation failed
    }
  };

  if (isLoading) {
    return <Spin spinning={true} />;
  }

  return (
    <>
      <TabHeader>
        <TabDescription>
          {t("productsTab.descriptionBefore")} <strong>{t("productsTab.descriptionCategories")}</strong>{t("productsTab.descriptionAfter")}
        </TabDescription>
        <div style={{ display: "flex", gap: "8px" }}>
          {isDeveloper && (
            <Button
              icon={<ImportOutlined />}
              onClick={() => setImportModalOpen(true)}
            >
              {t("productsTab.importFromRestaumatic")}
            </Button>
          )}
          <Button
            ref={addCategoryBtnRef}
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCategory}
            loading={isCreatingCategory}
          >
            {t("productsTab.addCategory")}
          </Button>
        </div>
      </TabHeader>

      <CategoriesContainer>
        {categories.map((category, index) => (
          <div
            key={category.id}
            ref={(el) => { if (el) categoryRefs.current[category.id] = el; }}
          >
            <ProductCategorySection
              category={category}
              onMoveUp={() => handleMoveCategoryUp(category.id)}
              onMoveDown={() => handleMoveCategoryDown(category.id)}
              canMoveUp={index > 0}
              canMoveDown={index < categories.length - 1}
              addProductBtnRef={index === 0 ? addProductBtnRef : undefined}
              scrollToProductId={category.id === scrollTargetCategoryId ? scrollTargetProductId : null}
              onScrollComplete={category.id === scrollTargetCategoryId ? () => {
                setScrollTargetCategoryId(null);
                setScrollTargetProductId(null);
              } : undefined}
            />
          </div>
        ))}
      </CategoriesContainer>

      <ImportFromUrlModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
      />

      <Modal
        title={t("productsTab.addCategoryModalTitle")}
        open={addCategoryModalOpen}
        onOk={handleAddCategorySubmit}
        onCancel={() => {
          addCategoryForm.resetFields();
          setAddCategoryModalOpen(false);
        }}
        closable={!isTourOpen}
        maskClosable={!isTourOpen}
        keyboard={!isTourOpen}
        okText={t("productsTab.create")}
        cancelText={t("productsTab.cancel")}
        confirmLoading={isCreatingCategory}
        width={480}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: 'flex', justifyContent: isTourOpen ? 'flex-end' : 'space-between' }}>
            {!isTourOpen && <CancelBtn />}
            <span ref={categoryModalOkBtnRef} style={{ display: "inline-block" }}>
              <OkBtn />
            </span>
          </div>
        )}
      >
        <div style={{ marginBottom: "20px", color: colors.textSecondary, fontSize: "14px" }}>
          {t("productsTab.categoryModalDescription")}
        </div>
        <Form form={addCategoryForm} layout="vertical" autoComplete="off">
          <Form.Item
            name="name"
            label={t("productsTab.categoryNameLabel")}
            rules={[{ required: true, message: t("productsTab.categoryNameRequired") }]}
          >
            <Input
              ref={categoryNameInputRef}
              placeholder={t("productsTab.categoryNamePlaceholder")}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
